import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { IndicatorType } from '@risksmart-app/domain/src/types/consts/indicator-type';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import _ from 'lodash';
import { useMemo } from 'react';
import {
  getAllContributorsCellValue,
  getAllOwnersCellValue,
  getAllViewersCellValue,
} from 'src/rbac/contributorHelper';

import useEntityInfo from '@/hooks/getEntityInfo';
import { useCommonLookupLazy } from '@/hooks/useCommonLookupLazy';
import { getFriendlyId } from '@/utils/friendlyId';
import { getTestScheduleStatus } from '@/utils/table/utils/testScheduleStatusHelper';

import {
  conformanceRatingFromResults,
  getConformanceTrendRating,
  getFrozenConformanceTrend,
} from './calculateConformanceRating';
import {
  latestResultValueFromData,
  previousResultValueFromData,
} from './latestResultValueFromData';
import {
  parentTitleFromLinkedParents,
  parentTypesFromLinkedParents,
} from './parentHelpers';
import {
  computeThreeMonthAverage,
  formatThreeMonthAverage,
} from './threeMonthAverage';
import type { IndicatorFlatFields, IndicatorTableFields } from './types';

export const useLabelledFields = (
  records: IndicatorFlatFields[] | undefined,
  // CUS-188: trend's OUTPUT shape (the ConformanceTrend enum) is identical
  // either way, so branching the computation here — rather than in the
  // cell renderer, as ConformanceLabelled does — needs no further changes
  // anywhere else the trend value is consumed (badge, PDF export style).
  // Defaults false so this hook's OTHER callers (the universal-widget
  // cross-entity join sources — indicatorControl.ts, indicatorObligation.ts,
  // indicatorRisk.ts — none of which are in CUS-188's named surface list)
  // keep their exact current legacy-only behaviour unchanged.
  isConfigurableThresholdsEnabled: boolean = false
) => {
  const { getLabel: getConformanceLabel } = useRating(
    'indicator_conformance_status'
  );
  const { getByValue: getCommonLookupByValue } = useCommonLookupLazy();
  const getEntityInfo = useEntityInfo();
  const { getLabel: trendGetLabel } = useRating('indicator_conformance_trend');
  const { getLabel: getLabelTestScheduleStatus } = useRating(
    'test_schedule_status'
  );

  return useMemo<IndicatorTableFields[] | undefined>(() => {
    return records?.map((d) => {
      // Numeric only — see the register's Conformance cell: an unstamped
      // text/boolean indicator would trend as indeterminate rather than
      // keeping its legacy target-match trend.
      const conformanceTrendValue =
        isConfigurableThresholdsEnabled && d.Type === IndicatorType.Number
          ? getFrozenConformanceTrend(d.orderedResults)
          : getConformanceTrendRating(d, d.orderedResults);
      const testScheduleStatus = getTestScheduleStatus(
        d.scheduleState?.OverdueDate,
        d.scheduleState?.DueDate
      );
      // `computeThreeMonthAverage` defaults `now` to `new Date()` at memo-eval
      // time. That means the 90-day window cutoff is fixed for the lifetime of
      // a `records` reference. Acceptable trade-off: the indicator register
      // page typically re-fetches on navigation, and any meaningful staleness
      // requires the tab to stay mounted across a UTC midnight. If that
      // scenario becomes load-bearing, inject a `now` that refreshes daily.
      const threeMonthAverage = computeThreeMonthAverage(
        d.Type,
        d.orderedResults
      );

      return {
        ...d,
        ParentTitle: parentTitleFromLinkedParents(d, getEntityInfo) || '-',
        ParentType:
          (parentTypesFromLinkedParents(d, getEntityInfo) || []).join(', ') ||
          '-',
        CreatedByUserName: d.createdBy?.FriendlyName || '-',
        ModifiedByUserName: d.modifiedBy?.FriendlyName || '-',
        LatestResultLabelled: latestResultValueFromData(d) || '-',
        PreviousResultLabelled: previousResultValueFromData(d) || '-',
        LatestResultDateLabelled: d.orderedResults[0]?.ResultDate || '-',
        Conformance: conformanceRatingFromResults(d),
        ConformanceLabelled: getConformanceLabel(
          conformanceRatingFromResults(d)
        ),
        ConformanceTrendValue: conformanceTrendValue,
        ConformanceTrend: trendGetLabel(conformanceTrendValue) || 'Static',
        TestFrequencyLabelled:
          getCommonLookupByValue('frequency', d.schedule?.Frequency)?.label ??
          '-',
        SequentialIdLabel: d.SequentialId
          ? getFriendlyId(Parent_Type_Enum.Indicator, d.SequentialId)
          : '',
        ThreeMonthAverage: formatThreeMonthAverage(threeMonthAverage),
        ThreeMonthAverageState: threeMonthAverage.kind,
        ThreeMonthAverageValue:
          threeMonthAverage.kind === 'value' ? threeMonthAverage.value : null,
        allOwners: getAllOwnersCellValue(d),
        allContributors: getAllContributorsCellValue(d),
        allViewers: getAllViewersCellValue(d),
        NextTestDate: d.scheduleState?.DueDate ?? '-',
        NextTestOverdueDate: d.scheduleState?.OverdueDate ?? '-',
        TestScheduleStatus: testScheduleStatus,
        TestScheduleStatusLabelled:
          getLabelTestScheduleStatus(testScheduleStatus) || testScheduleStatus,
      };
    });
  }, [
    records,
    isConfigurableThresholdsEnabled,
    getEntityInfo,
    getConformanceLabel,
    trendGetLabel,
    getCommonLookupByValue,
    getLabelTestScheduleStatus,
  ]);
};
